import { FC } from "react";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { Box, Chip, Typography } from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { stockReportAvailabilityState, textColorState } from "@/lib/store";
import { Stock } from "@/models/stock";

interface Props {
    stock: Stock;
    // Tighter padding / smaller text variant for the mobile slot next
    // to the company name. Desktop uses a slightly more spacious chip
    // alongside the market-hours session indicator.
    compact?: boolean;
}

const NEW_BADGE_WINDOW_DAYS = 10;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * "Stock Report Available" pill rendered in the page top-bar (next to
 * the company name on mobile, next to the market-hours session
 * indicator on desktop). Reads from ``stockReportAvailabilityState``
 * which is populated by ``RootLayout`` whenever a stock-page mounts.
 *
 * When the report's ``generatedAt`` is within the last 10 days, an
 * additional "New" tag is appended so readers spot freshly-published
 * coverage at a glance.
 *
 * Click → routes to the stock-report sub-page where the full report
 * renders. Renders nothing when no report exists; never lays out
 * placeholder space so the top-bar doesn't jump when the existence
 * check resolves.
 */
const StockReportBadge: FC<Props> = ({ stock, compact = false }) => {
    const router = useRouter();
    const textColor = useAtomValue(textColorState);
    const availability = useAtomValue(stockReportAvailabilityState);

    if (!availability || !availability.exists) return null;
    if (!stock?.ticker || !stock?.exchangesymbol) return null;

    // "New" if generated in the last 10 days. We compare against
    // ``generatedAt`` (back-end-stamped LLM completion time) rather
    // than ``publishedAt`` because the report becomes meaningful the
    // moment it's generated; "new" should reflect freshness of the
    // analysis, not when a downstream pacing job flipped status.
    let isNew = false;
    if (availability.generatedAt) {
        const ageMs = Date.now() - new Date(availability.generatedAt).getTime();
        isNew = ageMs >= 0 && ageMs < NEW_BADGE_WINDOW_DAYS * MS_PER_DAY;
    }

    const target = availability.deepLinkPath || `/s/${stock.exchangesymbol}/${stock.ticker}/stock-report`;

    // Mobile (`compact`) renders the badge on its own line under
    // the subtitle (see RootLayout) rather than inline with the H1,
    // so it can't crowd the company name. Same pill chrome as
    // desktop, just slightly tighter padding and a top margin to
    // separate it from the subtitle above.
    if (compact) {
        return (
            <Box
                onClick={() => router.push(target)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && router.push(target)}
                aria-label={isNew ? "New Stock Report available" : "Stock Report available"}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    mt: 0.75,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "primary.main",
                    color: "var(--accent-text, #31aba6)",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "primary.main", color: textColor },
                }}
            >
                <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                    <ArticleIcon sx={{ fontSize: 14 }} />
                    {isNew && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: -1,
                                right: -2,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "error.main",
                            }}
                        />
                    )}
                </Box>
                <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.3 }}>
                    Stock Report Available
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 14, ml: 0.25 }} />
            </Box>
        );
    }

    // Desktop variant mirrors the StockHeaderSession chip family
    // (MUI outlined Chip, captionSessionChip typography, icon-left)
    // so the badge reads as a sibling of the market-hours chip
    // rather than a one-off pill. Primary colour (instead of the
    // session chip's neutral) calls light attention without
    // shouting. "New" is conveyed by a small error-coloured dot to
    // stay consistent with the compact variant.
    return (
        <Chip
            size="small"
            variant="outlined"
            color="primary"
            clickable
            icon={
                <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                    <ArticleIcon sx={{ fontSize: 14 }} />
                    {isNew && (
                        <Box
                            component="span"
                            sx={{
                                position: "absolute",
                                top: -1,
                                right: -2,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "error.main",
                            }}
                        />
                    )}
                </Box>
            }
            label={
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="captionSessionChip" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                        Stock Report Available
                    </Typography>
                    <ChevronRightIcon sx={{ fontSize: 16, ml: 0.25 }} />
                </Box>
            }
            onClick={() => router.push(target)}
            onKeyDown={(e) => e.key === "Enter" && router.push(target)}
            aria-label={isNew ? "New Stock Report available" : "Stock Report available"}
            sx={{
                mx: 2,
                px: 0.5,
                cursor: "pointer",
                "& .MuiChip-icon": { color: "var(--accent-text, #31aba6)", ml: 0.5 },
                "&:hover": {
                    backgroundColor: "primary.main",
                    color: textColor,
                    "& .MuiChip-icon": { color: textColor },
                },
            }}
        />
    );
};

export default StockReportBadge;
